<template>
  <div class="plant-history-filters">
    <div class="filter-group">
      <div class="filter-group-header">
        <label class="filter-label">Date Range</label>
        <Button v-if="beginDate || endDate" fill-mode="link" class="clear-btn" @click="clearDates"> Clear </Button>
      </div>
      <div class="date-range">
        <DatePicker
          :max="new Date()"
          :value="beginDate as any"
          placeholder="Start Date"
          @change="beginDate = $event.value"
        />
        <span class="date-separator">to</span>
        <DatePicker
          :max="new Date()"
          :value="endDate as any"
          placeholder="End Date"
          @change="endDate = $event.value"
        />
      </div>
    </div>
    <div class="filter-group">
      <label class="filter-label">Item Types</label>
      <div class="checkbox-group">
        <div class="checkbox-group__row">
          <Checkbox v-model="filterVisits" label="Visits" />
          <Checkbox v-model="filterComments" label="Visit Comments" />
        </div>
        <div class="checkbox-group__row">
          <Checkbox v-model="filterIngredients" label="Ingredients" />
          <Checkbox v-model="filterProducts" label="Products" />
        </div>
      </div>
    </div>
    <div class="filter-group">
      <label class="filter-label">Visit Type</label>
      <DropDownList
        class="filter-dropdown"
        :textField="'text'"
        :data-items="visitTypeOptions"
        v-model="selectedVisitType"
        data-cy="visit-type-filter"
      />
    </div>
    <div class="filter-actions">
      <Button theme-color="primary" @click="applyFilters">Apply</Button>
      <Button @click="resetFilters">Reset</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { Button } from "@progress/kendo-vue-buttons";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import { ref } from "vue";
import { SessionCacheService } from "@/shared/services/SessionCacheService";
import moment from "moment";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { FilterGroup } from "@/Domain/FilterGroup";
import type { FilterField } from "@/Domain/FilterField";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import { EnumPlantHistoryType } from "@/Domain/EnumPlantHistoryType";

export interface Props {
  cachePrefix: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: "apply", filters: FilterExpression): void;
  (event: "clear"): void;
}>();

const visitTypeOptions = [
  { text: "All Types", value: "" },
  { text: "Regular", value: EnumVisitType.Regular },
  { text: "Initial", value: EnumVisitType.Initial },
  { text: "Passover", value: EnumVisitType.Passover },
  { text: "Special Production", value: EnumVisitType.SpecialProduction },
  { text: "Review", value: EnumVisitType.Review },
];

const defaultBeginDate = moment().subtract(1, "year").toDate();
const defaultEndDate = new Date();

const getSessionDate = (key: string): Date | null => {
  const value = SessionCacheService.get(key);
  return value ? new Date(value as any) : null;
};

const beginDate = ref<Date | null>(
  SessionCacheService.has(`${props.cachePrefix}beginDate`)
    ? getSessionDate(`${props.cachePrefix}beginDate`)
    : defaultBeginDate
);
const endDate = ref<Date | null>(
  SessionCacheService.has(`${props.cachePrefix}endDate`)
    ? getSessionDate(`${props.cachePrefix}endDate`)
    : defaultEndDate
);
const selectedVisitType = ref<{ text: string; value: string | EnumVisitType }>(
  SessionCacheService.get(`${props.cachePrefix}visitType`) ?? { text: "All Types", value: "" }
);
const filterVisits = ref<boolean>(SessionCacheService.get(`${props.cachePrefix}filterVisits`) ?? true);
const filterComments = ref<boolean>(SessionCacheService.get(`${props.cachePrefix}filterComments`) ?? false);
const filterIngredients = ref<boolean>(SessionCacheService.get(`${props.cachePrefix}filterIngredients`) ?? false);
const filterProducts = ref<boolean>(SessionCacheService.get(`${props.cachePrefix}filterProducts`) ?? false);

const getHistoryTypeFilters = () => {
  const types: number[] = [];
  if (filterVisits.value) types.push(EnumPlantHistoryType.VisitInspection);
  if (filterComments.value) types.push(EnumPlantHistoryType.VisitComment);
  if (filterIngredients.value) types.push(EnumPlantHistoryType.IngredientLineItem);
  if (filterProducts.value) types.push(EnumPlantHistoryType.ProductLineItem);
  return types;
};

const getFilterExpression = (): FilterExpression => {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  // Add history type filter
  const historyTypes = getHistoryTypeFilters();
  if (historyTypes.length > 0 && historyTypes.length < 4) {
    const typeFilterGroup = {
      FilterFields: historyTypes.map((type: number) => ({
        Operator: "eq",
        Value: type,
      })) as FilterField[],
      BooleanLogicOperator: EnumFilterStatementConnector.Or,
      FieldName: "Type",
      InternalFieldName: "",
    } as FilterGroup;
    filterModel.FilterGroups.push(typeFilterGroup);
  }

  // Add visit type filter
  if (selectedVisitType.value.value !== "") {
    const visitTypeFilterGroup = {
      FilterFields: [
        {
          Operator: "eq",
          Value: selectedVisitType.value.value,
        },
      ] as FilterField[],
      BooleanLogicOperator: EnumFilterStatementConnector.And,
      FieldName: "VisitType",
      InternalFieldName: "",
    } as FilterGroup;
    filterModel.FilterGroups.push(visitTypeFilterGroup);
  }

  return filterModel;
};

const applyFilters = () => {
  SessionCacheService.set(`${props.cachePrefix}beginDate`, beginDate.value);
  SessionCacheService.set(`${props.cachePrefix}endDate`, endDate.value);
  SessionCacheService.set(`${props.cachePrefix}visitType`, selectedVisitType.value);
  SessionCacheService.set(`${props.cachePrefix}filterVisits`, filterVisits.value);
  SessionCacheService.set(`${props.cachePrefix}filterComments`, filterComments.value);
  SessionCacheService.set(`${props.cachePrefix}filterIngredients`, filterIngredients.value);
  SessionCacheService.set(`${props.cachePrefix}filterProducts`, filterProducts.value);
  emit("apply", getFilterExpression());
};

const clearDates = () => {
  beginDate.value = null;
  endDate.value = null;
};

const resetFilters = () => {
  beginDate.value = defaultBeginDate;
  endDate.value = defaultEndDate;
  selectedVisitType.value = { text: "All Types", value: "" };
  filterVisits.value = true;
  filterComments.value = true;
  filterIngredients.value = true;
  filterProducts.value = true;

  SessionCacheService.set(`${props.cachePrefix}beginDate`, beginDate.value);
  SessionCacheService.set(`${props.cachePrefix}endDate`, endDate.value);
  SessionCacheService.set(`${props.cachePrefix}visitType`, selectedVisitType.value);
  SessionCacheService.set(`${props.cachePrefix}filterVisits`, true);
  SessionCacheService.set(`${props.cachePrefix}filterComments`, true);
  SessionCacheService.set(`${props.cachePrefix}filterIngredients`, true);
  SessionCacheService.set(`${props.cachePrefix}filterProducts`, true);

  emit("clear");
};

defineExpose({
  beginDate,
  endDate,
  getFilterExpression,
});
</script>

<style lang="scss" scoped>
.plant-history-filters {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &-header {
      display: flex;
      align-items: flex-end;
    }
  }

  .filter-label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0px;
  }

  .clear-btn {
    font-size: 12px;
    padding: 0;
    color: #0d6efd;
    margin-left: 8px;

    &:hover {
      text-decoration: underline;
    }
  }

  .date-range {
    display: flex;
    align-items: center;
    gap: 8px;

    .date-separator {
      color: #6b7280;
      font-weight: 500;
    }
  }

  .checkbox-group {
    display: flex;
    gap: 16px;
    align-items: center;

    &__row {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }

  .filter-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
    align-self: flex-end;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    .filter-group {
      width: 100%;
    }

    .date-range {
      flex-wrap: wrap;
    }

    .checkbox-group {
      flex-wrap: wrap;
      gap: 8px;
    }

    .filter-actions {
      margin-left: 0;
      justify-content: stretch;
    }
  }
}
</style>
